import { Button } from '~/components/primitives/Button'
import type { ResetPreview } from '~/services/resetService'
import { STORE } from '~/storage/schema'
import styles from './ResetSection.module.css'

/**
 * Full reset (D-067).
 *
 * **Not an `EditableSection`.** That component is a summary → reveal-form → Save
 * idiom for changing a stored value, and its affordances read as reversible.
 * This follows the arm-then-confirm pattern from `MeasurementHistory` instead:
 * the confirmation **states what will be lost** rather than asking "are you
 * sure?", because there is no undo (D-064).
 *
 * The component never touches `window` or the database. Reading the preview and
 * reloading afterwards are both the page's job, which keeps this testable and
 * keeps the one navigation side effect in one place.
 */

interface ResetSectionProps {
  /** Read when the section is armed. `undefined` until then — reset is the one
      control that must not read the whole database on every Settings render. */
  preview: ResetPreview | undefined
  onArm: () => void
  onCreateBackup: () => void
  backupBusy: boolean
  resetBusy: boolean
  problem: string | undefined
  onCancel: () => void
  onConfirm: () => void
}

export function ResetSection({
  preview,
  onArm,
  onCreateBackup,
  backupBusy,
  resetBusy,
  problem,
  onCancel,
  onConfirm,
}: ResetSectionProps) {
  const armed = preview !== undefined

  return (
    <section aria-labelledby="reset-heading">
      <h2 id="reset-heading" class={styles.sectionHeading}>
        Reset Project Sola
      </h2>

      {armed ? (
        <ResetConfirmation
          preview={preview}
          busy={resetBusy}
          problem={problem}
          onCancel={onCancel}
          onConfirm={onConfirm}
        />
      ) : (
        <>
          <p class={styles.note}>
            Resetting deletes everything Project Sola has stored on this device and returns it to
            first-run setup. The app stays installed and still works offline. The only way back is a
            backup file you already saved somewhere else.
          </p>

          <Button variant="secondary" onClick={onCreateBackup} busy={backupBusy} fullWidth>
            Create a backup first
          </Button>

          <div class={styles.armRow}>
            <Button
              variant="secondary"
              onClick={onArm}
              label="Reset Project Sola and delete all data on this device"
              fullWidth
            >
              Reset Project Sola
            </Button>
          </div>
        </>
      )}
    </section>
  )
}

interface ResetConfirmationProps {
  preview: ResetPreview
  busy: boolean
  problem: string | undefined
  onCancel: () => void
  onConfirm: () => void
}

/**
 * The counts are real, read from the database when the section was armed.
 *
 * "Everything else" rolls up the remaining stores rather than omitting them, so
 * the list cannot imply that only the four named things are removed — the same
 * never-silently-truncate rule the measurement history follows.
 */
function ResetConfirmation({
  preview,
  busy,
  problem,
  onCancel,
  onConfirm,
}: ResetConfirmationProps) {
  const { counts, totalRecords, blobCount, lastBackupAt } = preview

  const named = [
    { label: 'Weigh-ins', value: counts[STORE.weightEntries] ?? 0 },
    { label: 'Measurement sessions', value: counts[STORE.measurementSessions] ?? 0 },
    { label: 'Weekly reviews', value: counts[STORE.weeklyReviews] ?? 0 },
  ]
  const namedTotal = named.reduce((sum, row) => sum + row.value, 0)
  const everythingElse = Math.max(0, totalRecords - namedTotal)

  return (
    <div class={styles.dangerCard}>
      <h3 class={styles.cardHeading}>This removes, from this device:</h3>

      <dl class={styles.detail}>
        {named.map((row) => (
          <div class={styles.row} key={row.label}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
        <div class={styles.row}>
          <dt>Photos and files</dt>
          <dd>{blobCount}</dd>
        </div>
        <div class={styles.row}>
          <dt>Everything else</dt>
          <dd>{everythingElse}</dd>
        </div>
      </dl>

      <p class={styles.note}>
        Your profile, current phase, goal and display settings go with them. Project Sola restarts
        at first-run setup.
      </p>

      <p class={styles.warning}>
        {lastBackupAt === undefined ? (
          <>
            <strong>You have never made a backup.</strong> Nothing you delete here can be recovered.
          </>
        ) : (
          <>
            Recovery is only possible from a backup file you already saved. Last backup:{' '}
            <strong>{new Date(lastBackupAt).toLocaleString()}</strong>.
          </>
        )}
      </p>

      {problem === undefined ? null : (
        <p class={styles.problem} role="alert">
          Nothing was deleted. {problem}
        </p>
      )}

      <div class={styles.actions}>
        <Button variant="secondary" onClick={onCancel}>
          Keep my data
        </Button>
        <Button onClick={onConfirm} busy={busy} busyLabel="Deleting…">
          Delete everything
        </Button>
      </div>
    </div>
  )
}
